import type {
  Discipline, DisciplineFilter, UserRole, Workflow, WorkflowExecutor, WorkflowSortKey,
} from './types'
import { AGENT_ALIASES, AgentId, getAgentAvatarImage } from '@/constants/agentNames'

/**
 * The library filters on exactly three discipline chips (Figma "2. Workflow
 * Selection"): Chemical / Electrical / Mechanical. The backend now assigns
 * every workflow to the nearest of these three at CREATION — keyword-first,
 * model-judged for ambiguous cases (workflow-discipline.ts; Pegah/Rahul
 * feedback 2026-07-15). So the client only recognizes the three chips and maps
 * anything else to null — an unknown string (or a casing drift from the
 * backend contract, which emits lowercase) can't masquerade as a Discipline
 * and silently break the filter.
 */
const FILTERABLE_DISCIPLINES: ReadonlySet<Discipline> = new Set<Discipline>([
  'chemical', 'electrical', 'mechanical',
])

function normalizeDiscipline(value: string | null | undefined): Discipline | null {
  return value && FILTERABLE_DISCIPLINES.has(value as Discipline) ? (value as Discipline) : null
}

// Current viewer role (mock). Members can't manage workflows, so the row
// kebab menu is hidden for them (Figma §1 "Roles — Member"). Swap for the
// real role from auth/session when the backend lands.
/**
 * Fallback when the auth store has no role claim (older tokens): admin, which
 * matches the pre-role behavior. Live gating passes the REAL role from
 * `useAppStore().user.role` (same source the Settings pages use).
 */
export const CURRENT_ROLE: UserRole = 'admin'
// Accepts the auth store's role claim too (types/users.UserRole includes
// 'annotator', which this local union predates) — any non-member manages;
// annotators are excluded by the caller's `!isAnnotator` clause.
export const canManageWorkflows = (role: UserRole | string = CURRENT_ROLE) => role !== 'member'

/**
 * Resolve a workflow's executor avatars. Each executor (an agentic step) gets
 * its persona photo when the name maps to a known agent (e.g. "AI Chemical
 * Engineer"). The troubleshooting templates name their steps by PHASE ("Define
 * The Problem", …) — no persona to resolve — so when nothing maps, we show the
 * single AI Plant Support Engineer persona that actually drives the run, rather
 * than a row of step-title initials. (The Figma cards show an agent face.)
 */
function buildExecutors(raw: { id: string; name: string }[]): WorkflowExecutor[] {
  const mapped = raw.map((e) => ({ id: e.id, name: e.name, avatarUrl: getAgentAvatarImage(e.name) }))
  if (mapped.length > 0 && mapped.every((e) => !e.avatarUrl)) {
    return [
      {
        id: AgentId.PLANT_SUPPORT,
        name: AGENT_ALIASES[AgentId.PLANT_SUPPORT].displayName,
        avatarUrl: getAgentAvatarImage(AgentId.PLANT_SUPPORT),
      },
    ]
  }
  return mapped
}

/**
 * Map a backend template summary onto the list-card shape. Uses the REAL
 * metadata the API now returns — executors (the workflow's AI agents), created,
 * last-opened, and discipline — instead of the old hardcoded placeholders.
 * Fields are null when there's no real value yet (built-ins have no created
 * date; a never-run workflow has no last-opened); the UI renders honest empty
 * states rather than a fake epoch.
 */
export function templateToCard(
  summary: {
    id: string
    title: string
    description: string
    builtin?: boolean
    executors?: { id: string; name: string }[]
    createdAt?: string | null
    createdBy?: string | null
    lastOpenedAt?: string | null
    discipline?: string | null
  },
  opts: { draft?: boolean } = {},
): Workflow {
  return {
    id: summary.id,
    name: summary.title,
    description: summary.description,
    discipline: normalizeDiscipline(summary.discipline),
    executors: buildExecutors(summary.executors ?? []),
    createdAt: summary.createdAt ?? null,
    lastOpenedAt: summary.lastOpenedAt ?? null,
    builtin: summary.builtin === true,
    // Built-ins are global Templates; tenant-authored rows are the user's own.
    createdByMe: summary.builtin !== true,
    createdBy: summary.createdBy ?? null,
    ...(opts.draft ? { draft: true } : {}),
  }
}

/**
 * Merge the published list with draft-only rows: a template that exists in both
 * (published + a pending draft version) shows once as published; a template with
 * ONLY a draft version shows once with the Draft badge. Built-ins in the draft
 * response are duplicates of the published call and are dropped. Pure; unit-tested.
 */
export function mergePublishedAndDrafts(
  published: Workflow[],
  drafts: Workflow[],
): Workflow[] {
  const publishedIds = new Set(published.map((w) => w.id))
  const draftOnly = drafts.filter((w) => !w.builtin && !publishedIds.has(w.id))
  return [...published, ...draftOnly]
}

/**
 * Fetch the real published workflow templates (built-ins + tenant store) as
 * list cards. Returns an empty list (NOT fixtures) if the API errors, so the UI
 * never shows mock data — an empty workflow surface is honest about the state.
 */
/**
 * userId → display name for the tenant, from the same directory the settings
 * recipient picker reads. Prefers the name, falls back to the email (a person is
 * still identifiable by it); an id with no directory entry is simply absent, so the
 * caller renders a dash instead of a UUID.
 */
async function authorNamesByUserId(): Promise<Map<string, string>> {
  try {
    const { apiService } = await import('@/service/api')
    const { users } = await apiService.getOrganizationUsers()
    const byId = new Map<string, string>()
    for (const user of users ?? []) {
      const label = (user.name ?? '').trim() || (user.email ?? '').trim()
      if (user.user_id && label) byId.set(user.user_id, label)
    }
    return byId
  } catch {
    return new Map()
  }
}

export async function listWorkflowTemplates(): Promise<Workflow[]> {
  try {
    const { listWorkflowTemplates: fetchTemplates } = await import('./workflowApi')
    // Published templates + saved-but-unpublished drafts (the Draft badge in the
    // library). The drafts call is best-effort: if it fails, the published list
    // still renders.
    // The row stores the author's USER ID; the list must show a person, resolved from
    // the tenant directory the settings panel already uses. It depends on neither
    // template call, so it rides the SAME Promise.all — awaited after them it added a
    // sequential round-trip to first paint on a list surface. Best-effort: if the
    // directory call fails the column falls back to "—" rather than a raw UUID.
    const [{ templates }, draftRows, names] = await Promise.all([
      fetchTemplates(),
      fetchTemplates('draft').then(
        (r) => r.templates ?? [],
        () => [],
      ),
      authorNamesByUserId(),
    ])
    const withAuthor = (workflow: Workflow): Workflow => ({
      ...workflow,
      createdByName: workflow.createdBy ? (names.get(workflow.createdBy) ?? null) : null,
    })
    return mergePublishedAndDrafts(
      (templates ?? []).map((s) => withAuthor(templateToCard(s))),
      draftRows.map((s) => withAuthor(templateToCard(s, { draft: true }))),
    )
  } catch {
    return []
  }
}

/**
 * Duplicate a workflow into a new tenant-owned copy (real). The server resolves
 * the trusted source definition and creates a published copy — this works for
 * built-ins with verify/fanout steps too (the authoring endpoint would reject
 * those). The list reload reflects the new card.
 */
export async function duplicateWorkflow(id: string): Promise<Workflow | null> {
  const api = await import('./workflowApi')
  const row = (await api.duplicateWorkflowTemplate(id)) as { templateId: string; title?: string }
  return templateToCard({ id: row.templateId, title: row.title ?? 'Copy', description: '', builtin: false })
}

/**
 * The id of the most recent run for a workflow, or null. Lets the list reopen a
 * workflow into its last run (graph + chat restored) instead of a blank builder.
 */
export async function latestRunIdForWorkflow(id: string): Promise<string | null> {
  try {
    const { getLatestRunForTemplate } = await import('./workflowApi')
    const { run } = await getLatestRunForTemplate(id)
    return run?.id ?? null
  } catch {
    return null
  }
}

/** Delete every version of a tenant workflow (real). True when a row was removed. */
export async function deleteWorkflow(id: string): Promise<boolean> {
  const api = await import('./workflowApi')
  const { deleted } = await api.deleteWorkflowTemplate(id)
  return deleted > 0
}

/**
 * Archive a workflow's CURRENT version (drops it from the live set; history
 * kept). The card doesn't carry a version, so resolve it from the list.
 */
export async function archiveWorkflow(id: string): Promise<boolean> {
  const api = await import('./workflowApi')
  const { templates } = await api.listWorkflowTemplates()
  const version = templates.find((tpl) => tpl.id === id)?.version
  if (!version) return false
  await api.archiveWorkflowTemplate(id, version)
  return true
}

/** Rename a tenant workflow (real). Returns the updated card, or null on failure. */
export async function renameWorkflow(id: string, name: string): Promise<Workflow | null> {
  const api = await import('./workflowApi')
  const row = await api.renameWorkflowTemplate(id, name)
  return templateToCard({ id: row.templateId ?? id, title: name, description: '', builtin: false })
}

// ─── Pure client-side filter + sort (also unit-tested) ────────────────────────

/** Filter by discipline + free-text search over name/description, then sort. */
/**
 * The suffix the server gives a duplicate — `${source.title} (copy)`, plain and
 * unnumbered (workflow.controller.ts). Duplicating twice yields two identically
 * named copies; duplicating a copy yields "X (copy) (copy)".
 *
 * The name is the ONLY link a copy has to its original. `duplicateTemplate`
 * writes `source: 'authored'` — a category, not a parent — and stores no id of
 * the row it copied, so there is nothing sturdier to match on from here. That
 * makes this coupled to the server's title format: change the suffix there and
 * copies stop grouping (silently, since nothing else breaks). A
 * `duplicated_from` column on the template row would end the coupling and let
 * this match on an id instead.
 */
const COPY_SUFFIX = ' (copy)'

/** The name a copy was made from, or null when this name is not a copy. */
function originalNameOf(name: string): string | null {
  return name.endsWith(COPY_SUFFIX) ? name.slice(0, -COPY_SUFFIX.length) : null
}

/**
 * UI-02: a duplicate lands next to the workflow it came from.
 *
 * The copy is created with a fresh `createdAt` and no `lastOpenedAt`, so every
 * date sort throws it to one end of the list — last under "Last used" (the
 * DEFAULT, where a never-run copy sorts as epoch 0) and first under "Newest".
 * Either way it is nowhere near the original, and with names that differ only
 * by a suffix there is nothing to scan for. Pegah hit this on "Troubleshooting
 * Base" in dev.
 *
 * So the copy is pulled to sit immediately after its original, whatever the
 * sort. Deliberately a reordering of an already-sorted list rather than a
 * comparator: the sort the user picked still decides where the ORIGINAL sits,
 * and only the copy moves.
 *
 * The trade-off, stated because it is a real one: under "Newest" the copy IS
 * the newest row and no longer appears first. Adjacency is what was asked for —
 * a copy you cannot find is worse than one a position away from where a date
 * sort would strictly put it — and the pair still sits at the original's
 * sorted position, so the ordering stays explicable.
 *
 * A copy whose original is not in the list — filtered out by search or
 * discipline, or deleted — keeps its own sorted position rather than being
 * orphaned to the end.
 */
function groupCopiesWithOriginals(items: Workflow[]): Workflow[] {
  const copiesOf = new Map<string, Workflow[]>()
  for (const w of items) {
    const original = originalNameOf(w.name)
    if (original === null) continue
    const siblings = copiesOf.get(original)
    if (siblings) siblings.push(w)
    else copiesOf.set(original, [w])
  }
  if (copiesOf.size === 0) return items

  const present = new Set(items.map((w) => w.name))
  const emitted = new Set<string>()
  const out: Workflow[] = []

  // Recursive so a copy of a copy chains: X, X (copy), X (copy) (copy).
  const emit = (w: Workflow) => {
    if (emitted.has(w.id)) return
    emitted.add(w.id)
    out.push(w)
    for (const copy of copiesOf.get(w.name) ?? []) emit(copy)
  }

  for (const w of items) {
    const original = originalNameOf(w.name)
    // Skip copies whose original is here — the original emits them, so the pair
    // sits at the ORIGINAL's position. Without this a "Newest" sort emits the
    // copy first and the original trails it.
    if (original !== null && present.has(original)) continue
    emit(w)
  }
  // Every row is emitted: a skipped copy has a present original, and that
  // original is either emitted by this loop or is itself a skipped copy whose
  // own original is present. Each hop drops a ` (copy)`, so the chain is finite
  // and ends at a row this loop emits — which then emits its copies. The
  // "nothing is lost or duplicated" spec pins that rather than a second pass.
  return out
}

export function selectWorkflows(
  workflows: Workflow[],
  opts: {
    search: string
    discipline: DisciplineFilter
    sortBy: WorkflowSortKey
    mine?: boolean
    /** 'mine' = user workflows only; undefined/'all' = everything incl. built-ins.
     *  (The 'templates' kind was removed with its filter chip — latest Figma.) */
    kind?: 'all' | 'mine'
  },
): Workflow[] {
  const q = opts.search.trim().toLowerCase()
  const filtered = workflows.filter((w) => {
    if (opts.kind === 'mine' && w.builtin) return false
    if (opts.mine && !w.createdByMe) return false
    if (opts.discipline !== 'all' && w.discipline !== opts.discipline) return false
    if (!q) return true
    return (
      w.name.toLowerCase().includes(q) ||
      w.description.toLowerCase().includes(q)
    )
  })

  // null timestamps (built-ins with no created date; never-run workflows) sort
  // last under date sorts — treat them as epoch 0.
  const ms = (iso: string | null) => (iso ? new Date(iso).getTime() : 0)
  const sorted = [...filtered]
  switch (opts.sortBy) {
    case 'newest':
      sorted.sort((a, b) => ms(b.createdAt) - ms(a.createdAt))
      break
    case 'name_az':
      sorted.sort((a, b) => a.name.localeCompare(b.name))
      break
    case 'name_za':
      sorted.sort((a, b) => b.name.localeCompare(a.name))
      break
    case 'last_used':
    default:
      sorted.sort((a, b) => ms(b.lastOpenedAt) - ms(a.lastOpenedAt))
      break
  }
  return groupCopiesWithOriginals(sorted)
}
